import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AdminContent } from '@/content/AdminContent';
import { useUsersForExport } from '@/lib/hooks/useUserProfile';
import { useRoleDirectory } from '@/lib/hooks/useRoleDirectory';
import { listGroupMembershipsForUsers } from '@/lib/services/memberships';
import {
  buildGroupMembershipsByUserId,
  downloadCSV,
  generateUsersCSV,
} from '@/lib/utils/export.utils';

type ExportUsersDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ExportUsersDialog({
  open,
  onOpenChange,
}: ExportUsersDialogProps) {
  useRoleDirectory();
  const [isLoadingMemberships, setIsLoadingMemberships] = useState(false);
  const { refetch: refetchForExport, isFetching: isExporting } =
    useUsersForExport();
  const isExportDisabled = isExporting || isLoadingMemberships;

  const handleExport = async () => {
    try {
      const { data: users } = await refetchForExport();
      if (!users) {
        return;
      }

      setIsLoadingMemberships(true);
      const memberships = await listGroupMembershipsForUsers(
        users.map((user) => user.id)
      );
      const membershipsByUserId = buildGroupMembershipsByUserId(memberships);
      const csvContent = generateUsersCSV(
        users.map((profile) => ({
          profile,
          groups: membershipsByUserId.get(profile.id) ?? [],
        }))
      );
      const filename = `users_${new Date().toISOString().split('T')[0]}.csv`;

      downloadCSV(csvContent, filename);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to export users', error);
    } finally {
      setIsLoadingMemberships(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {AdminContent.exports.exportUsersDialog.title}
          </DialogTitle>
          <DialogDescription>
            {AdminContent.exports.exportUsersDialog.description}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button
            className="mt-3"
            variant="outline"
            size="lg"
            onClick={() => onOpenChange(false)}
          >
            {AdminContent.exports.exportUsersDialog.cancel}
          </Button>
          <Button size="lg" onClick={handleExport} disabled={isExportDisabled}>
            {isExportDisabled
              ? 'Exporting...'
              : AdminContent.exports.exportUsersDialog.export}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
